import moment from 'moment-timezone';
import { extractRawRows } from './utilizationEngine';

const EVENT_TIME_FIELDS = [
  'timestamp',
  'event_time',
  'eventTime',
  'device_timestamp',
  'deviceTimestamp',
  'ts',
  'time',
  'datetime',
  'datetime_ist'
];

const INCOMING_TIME_FIELDS = [
  'incoming_time',
  'incomingTime',
  'ingestion_time',
  'ingestionTime',
  'server_time',
  'serverTime',
  'received_at',
  'receivedAt',
  'createdAt',
  'insertedAt',
  'incoming_datetime_ist'
];

const OPTIONAL_FIELD_CANDIDATES = [
  'location',
  'zone',
  'subLocation',
  'sub_location',
  'client',
  'lat_long',
  'latLong',
  'latitude',
  'longitude'
];

const DEFAULT_CADENCE_SECONDS = 10;

const pickFirstPresent = (row, keys) => {
  for (const key of keys) {
    if (row && row[key] !== undefined && row[key] !== null && row[key] !== '') {
      return row[key];
    }
  }
  return null;
};

const toEpochMs = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const numeric = Number(trimmed);
      if (!Number.isFinite(numeric)) return null;
      return numeric < 1e12 ? numeric * 1000 : numeric;
    }
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed.valueOf() : null;
};

const formatIst = (timestamp) => moment(timestamp).tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss');

const safePercentile = (values, percentile) => {
  const valid = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (valid.length === 0) return null;
  if (valid.length === 1) return valid[0];

  const index = (percentile / 100) * (valid.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;

  if (lower === upper) return valid[lower];
  return valid[lower] + (valid[upper] - valid[lower]) * weight;
};

const roundMetric = (value, digits = 2) => {
  if (!Number.isFinite(value)) return null;
  return Number(value.toFixed(digits));
};

const toPercent = (count, total) => {
  if (!total) return 0;
  return roundMetric((count / total) * 100, 2);
};

const classifyLagBand = (lagSeconds) => {
  if (lagSeconds <= 5) return 'prompt';
  if (lagSeconds <= 120) return 'near_rt';
  if (lagSeconds <= 1800) return 'delay';
  return 'heavy';
};

const classifyAvailabilityRating = (summary) => {
  const availability = Number(summary.availability_pct || 0);
  const largestGap = Number(summary.largest_data_gap_seconds || 0);
  const gapOver5m = Number(summary.gap_gt_5m || 0);
  const gapOver60s = Number(summary.gap_gt_60s || 0);

  if (availability >= 99.5 && largestGap <= 30 && gapOver60s === 0) {
    return {
      level: 'excellent',
      color: 'green',
      reason: 'Coverage is effectively complete and no meaningful gaps were detected.'
    };
  }

  if (availability >= 98 && largestGap <= 120 && gapOver5m === 0) {
    return {
      level: 'good',
      color: 'blue',
      reason: 'Coverage is strong with only short or isolated gaps.'
    };
  }

  if (availability >= 95 && largestGap <= 900) {
    return {
      level: 'degraded',
      color: 'orange',
      reason: 'Coverage is reduced or repeated medium gaps were detected.'
    };
  }

  return {
    level: 'poor',
    color: 'red',
    reason: 'Coverage is materially below expected cadence or long gaps are present.'
  };
};

export const normalizeLagRows = (payload, options = {}) => {
  const rawRows = extractRawRows(payload);
  const diagnostics = {
    totalRows: rawRows.length,
    validRows: 0,
    skippedRows: 0,
    missingEventTime: 0,
    missingIncomingTime: 0,
    invalidEventTime: 0,
    invalidIncomingTime: 0,
    negativeLagRows: 0
  };

  const rows = rawRows
    .map((row, index) => {
      const rawEventTime = pickFirstPresent(row, EVENT_TIME_FIELDS);
      const rawIncomingTime = pickFirstPresent(row, INCOMING_TIME_FIELDS);

      if (rawEventTime === null) {
        diagnostics.skippedRows += 1;
        diagnostics.missingEventTime += 1;
        return null;
      }
      if (rawIncomingTime === null) {
        diagnostics.skippedRows += 1;
        diagnostics.missingIncomingTime += 1;
        return null;
      }

      const eventTimestamp = toEpochMs(rawEventTime);
      const incomingTimestamp = toEpochMs(rawIncomingTime);

      if (!Number.isFinite(eventTimestamp)) {
        diagnostics.skippedRows += 1;
        diagnostics.invalidEventTime += 1;
        return null;
      }
      if (!Number.isFinite(incomingTimestamp)) {
        diagnostics.skippedRows += 1;
        diagnostics.invalidIncomingTime += 1;
        return null;
      }

      const lagSeconds = (incomingTimestamp - eventTimestamp) / 1000;
      if (!Number.isFinite(lagSeconds) || lagSeconds < 0) {
        diagnostics.skippedRows += 1;
        diagnostics.negativeLagRows += 1;
        return null;
      }

      const passthrough = {};
      OPTIONAL_FIELD_CANDIDATES.forEach((field) => {
        if (row[field] !== undefined && row[field] !== null && row[field] !== '') {
          passthrough[field] = row[field];
        }
      });

      diagnostics.validRows += 1;

      return {
        key: row._id || row.id || `${row.device_id || row.deviceId || 'device'}_${eventTimestamp}_${index}`,
        device_id: row.device_id || row.deviceId || row.tagDeviceId || null,
        timestamp: eventTimestamp,
        incoming_time: incomingTimestamp,
        lag_seconds: roundMetric(lagSeconds, 2),
        datetime_ist: formatIst(eventTimestamp),
        incoming_datetime_ist: formatIst(incomingTimestamp),
        ...passthrough,
        originalRow: row
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.timestamp - b.timestamp)
    .map((row, index, list) => {
      const previous = index > 0 ? list[index - 1] : null;
      const gapFromPrevSeconds = previous ? roundMetric((row.timestamp - previous.timestamp) / 1000, 2) : null;
      const inferredMissingFromPrev = gapFromPrevSeconds && gapFromPrevSeconds > DEFAULT_CADENCE_SECONDS
        ? Math.max(0, Math.floor(gapFromPrevSeconds / DEFAULT_CADENCE_SECONDS) - 1)
        : 0;

      return {
        ...row,
        gap_from_prev_seconds: gapFromPrevSeconds,
        inferred_missing_from_prev: inferredMissingFromPrev
      };
    });

  if (options.windowStartMs !== undefined && options.windowEndExclusiveMs !== undefined) {
    const cadenceMs = (options.cadenceSeconds || DEFAULT_CADENCE_SECONDS) * 1000;
    rows.forEach((row) => {
      const offsetMs = row.timestamp - options.windowStartMs;
      row.seconds_from_window_start = roundMetric(offsetMs / 1000, 2);
      row.slot_index = offsetMs >= 0 ? Math.floor(offsetMs / cadenceMs) : null;
    });
  }

  return { rows, diagnostics };
};

export const computeLagProfile = (normalizedRows) => {
  const values = normalizedRows.map((row) => row.lag_seconds).filter((value) => Number.isFinite(value));
  const bandCounts = {
    prompt: 0,
    near_rt: 0,
    delay: 0,
    heavy: 0
  };

  values.forEach((lagSeconds) => {
    bandCounts[classifyLagBand(lagSeconds)] += 1;
  });

  const recordCount = values.length;
  const sum = values.reduce((total, value) => total + value, 0);

  return {
    summary: {
      record_count: recordCount,
      min_lag: recordCount ? roundMetric(Math.min(...values), 2) : null,
      mean_lag: recordCount ? roundMetric(sum / recordCount, 2) : null,
      p50_lag: roundMetric(safePercentile(values, 50), 2),
      p75_lag: roundMetric(safePercentile(values, 75), 2),
      p90_lag: roundMetric(safePercentile(values, 90), 2),
      p95_lag: roundMetric(safePercentile(values, 95), 2),
      p99_lag: roundMetric(safePercentile(values, 99), 2),
      max_lag: recordCount ? roundMetric(Math.max(...values), 2) : null,
      pct_prompt_le_5s: toPercent(bandCounts.prompt, recordCount),
      pct_near_rt_5s_2m: toPercent(bandCounts.near_rt, recordCount),
      pct_delay_2m_30m: toPercent(bandCounts.delay, recordCount),
      pct_heavy_gt_30m: toPercent(bandCounts.heavy, recordCount),
      pct_gt_10m: toPercent(values.filter((value) => value > 600).length, recordCount),
      pct_gt_30m: toPercent(values.filter((value) => value > 1800).length, recordCount),
      pct_gt_60m: toPercent(values.filter((value) => value > 3600).length, recordCount)
    },
    bandCounts,
    bandPercentages: {
      prompt: toPercent(bandCounts.prompt, recordCount),
      near_rt: toPercent(bandCounts.near_rt, recordCount),
      delay: toPercent(bandCounts.delay, recordCount),
      heavy: toPercent(bandCounts.heavy, recordCount)
    }
  };
};

export const computeAvailabilityProfile = (normalizedRows, options = {}) => {
  const cadenceSeconds = options.cadenceSeconds || DEFAULT_CADENCE_SECONDS;
  const cadenceMs = cadenceSeconds * 1000;
  const windowStartMs = Number(options.windowStartMs);
  const windowEndExclusiveMs = Number(options.windowEndExclusiveMs);
  const hasWindow = Number.isFinite(windowStartMs) && Number.isFinite(windowEndExclusiveMs) && windowEndExclusiveMs > windowStartMs;
  const expectedPacketCount = hasWindow ? Math.round((windowEndExclusiveMs - windowStartMs) / cadenceMs) : 0;

  const diagnostics = {
    duplicateSlotRows: 0,
    burstRows: 0,
    outOfWindowRows: 0,
    uniqueCoveredSlots: 0
  };

  const coveredSlots = new Set();
  const primarySlotRowBySlot = new Map();
  const rowsInWindow = [];

  normalizedRows.forEach((row) => {
    if (!hasWindow || row.timestamp < windowStartMs || row.timestamp >= windowEndExclusiveMs) {
      diagnostics.outOfWindowRows += 1;
      return;
    }

    const slotIndex = Math.floor((row.timestamp - windowStartMs) / cadenceMs);
    const canonicalRow = {
      ...row,
      slot_index: slotIndex,
      is_primary_slot_sample: !coveredSlots.has(slotIndex)
    };

    if (coveredSlots.has(slotIndex)) {
      diagnostics.duplicateSlotRows += 1;
      diagnostics.burstRows += 1;
    } else {
      coveredSlots.add(slotIndex);
      primarySlotRowBySlot.set(slotIndex, canonicalRow);
    }

    rowsInWindow.push(canonicalRow);
  });

  diagnostics.uniqueCoveredSlots = coveredSlots.size;

  const uniqueSlots = Array.from(coveredSlots).sort((a, b) => a - b);
  const gaps = [];
  const gapDurations = [];
  let totalMissingPackets = 0;

  const boundaries = [-1, ...uniqueSlots, expectedPacketCount];
  for (let i = 1; i < boundaries.length; i += 1) {
    const previousSlot = boundaries[i - 1];
    const nextSlot = boundaries[i];
    const missingSlots = nextSlot - previousSlot - 1;
    if (missingSlots <= 0) continue;

    const startSlot = previousSlot + 1;
    const endSlot = nextSlot - 1;
    const gapStartMs = windowStartMs + (startSlot * cadenceMs);
    const gapEndExclusiveMs = windowStartMs + ((endSlot + 1) * cadenceMs);
    const durationSeconds = missingSlots * cadenceSeconds;
    const previousRow = previousSlot >= 0 ? primarySlotRowBySlot.get(previousSlot) : null;
    const nextRow = nextSlot < expectedPacketCount ? primarySlotRowBySlot.get(nextSlot) : null;

    totalMissingPackets += missingSlots;
    gapDurations.push(durationSeconds);
    gaps.push({
      key: `gap_${startSlot}_${endSlot}`,
      start_slot_index: startSlot,
      end_slot_index: endSlot,
      gap_start_timestamp: gapStartMs,
      gap_end_exclusive_timestamp: gapEndExclusiveMs,
      gap_start_ist: formatIst(gapStartMs),
      gap_end_ist: formatIst(gapEndExclusiveMs - 1000),
      gap_duration_seconds: durationSeconds,
      missing_packets: missingSlots,
      previous_event_time_ist: previousRow ? previousRow.datetime_ist : 'Window Start',
      next_event_time_ist: nextRow ? nextRow.datetime_ist : 'Window End'
    });
  }

  const receivedPacketCount = coveredSlots.size;
  const availabilityPct = expectedPacketCount ? toPercent(receivedPacketCount, expectedPacketCount) : 0;
  const totalGapSeconds = gapDurations.reduce((sum, value) => sum + value, 0);

  const summary = {
    expected_packet_count: expectedPacketCount,
    received_packet_count: receivedPacketCount,
    inferred_missing_packet_count: totalMissingPackets,
    availability_pct: availabilityPct,
    gap_event_count: gaps.length,
    total_data_gap_seconds: roundMetric(totalGapSeconds, 2),
    average_data_gap_seconds: gaps.length ? roundMetric(totalGapSeconds / gaps.length, 2) : 0,
    median_data_gap_seconds: roundMetric(safePercentile(gapDurations, 50), 2) || 0,
    largest_data_gap_seconds: gaps.length ? roundMetric(Math.max(...gapDurations), 2) : 0,
    gap_gt_30s: gaps.filter((gap) => gap.gap_duration_seconds > 30).length,
    gap_gt_60s: gaps.filter((gap) => gap.gap_duration_seconds > 60).length,
    gap_gt_5m: gaps.filter((gap) => gap.gap_duration_seconds > 300).length
  };

  const rating = classifyAvailabilityRating(summary);

  const enrichedRows = rowsInWindow.map((row) => {
    const primaryGap = gaps.find((gap) => gap.start_slot_index === row.slot_index + 1);
    return {
      ...row,
      missing_packets_after_row: primaryGap ? primaryGap.missing_packets : 0,
      gap_after_row_seconds: primaryGap ? primaryGap.gap_duration_seconds : 0
    };
  });

  return {
    summary,
    rating,
    gaps,
    diagnostics,
    rows: enrichedRows
  };
};

export const buildLagProfileReport = (payload, options = {}) => {
  const cadenceSeconds = options.cadenceSeconds || DEFAULT_CADENCE_SECONDS;
  const { rows, diagnostics } = normalizeLagRows(payload, options);
  const lagProfile = computeLagProfile(rows);
  const availability = computeAvailabilityProfile(rows, options);

  return {
    metadata: {
      assetId: options.assetId || null,
      assetCode: options.assetCode || null,
      windowStart: options.windowStartMs || null,
      windowEnd: options.windowEndMs || null,
      windowEndExclusive: options.windowEndExclusiveMs || null,
      windowStartIst: Number.isFinite(options.windowStartMs) ? formatIst(options.windowStartMs) : null,
      windowEndIst: Number.isFinite(options.windowEndMs) ? formatIst(options.windowEndMs) : null,
      cadenceSeconds
    },
    rows: availability.rows.length ? availability.rows : rows,
    diagnostics: {
      ...diagnostics,
      ...availability.diagnostics
    },
    bandCounts: lagProfile.bandCounts,
    bandPercentages: lagProfile.bandPercentages,
    summary: lagProfile.summary,
    availability
  };
};
